/**
 * Calling from the app (1.5.7) — the one place a screen gets a call link from. The link itself is built by
 * shared/calling.ts (normal · hide my number, *67 · through Teams); this reads the signed-in person's choice:
 * the server's (GET /api/me — their own switch under the gear, or the administrator's on Admin → Users), or
 * the copy kept on this device while the app cannot reach it. No screen writes a call address by hand.
 *
 * The signed-out link pages (a tenant tapping the office number) are not staff calls and do not use this.
 */
import { useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { MeResponse } from '@shared/admin';
import { callLink, defaultCallMode, effectiveCallMode, isCallMode, type CallMode, type CallingSettings } from '@shared/calling';
import { send, useMeExtras } from './admin';
import { ApiRequestError } from './api';
import { hasStoredPrefs, usePrefs } from './prefs';

/**
 * 1.8.3 (D-1.8.3-1): what this device knows of its person's calling choice — the account's last answer, or their own
 * press here. Kept beside the settings, in its own key, so that taking the account's answer never makes a new device
 * look as if its person had chosen settings on it (src/lib/my-settings.ts offers a device's own choices to the account).
 */
const knownKey = (userId: string | null) => `abf.call.${userId ?? 'device'}`;
export function knownCallMode(userId: string | null): CallMode | null {
  try {
    const v = localStorage.getItem(knownKey(userId));
    return isCallMode(v) ? v : null;
  } catch {
    return null;
  }
}
function rememberCallMode(userId: string | null, mode: CallMode | null): void {
  try {
    if (mode) localStorage.setItem(knownKey(userId), mode);
    else localStorage.removeItem(knownKey(userId));
  } catch {
    /* private mode: the account's answer is asked again next time */
  }
}

/** The signed-in person's calling choice as it applies now, and whether Teams is offered. */
export function useCalling(): CallingSettings {
  const me = useMeExtras();
  const { prefs, userId } = usePrefs();
  const server = me.data?.calling ?? null;
  const teams = server?.teams ?? false;
  // a change made here that the server has not taken yet wins; then the server; then this device's copy —
  // 1.8.3: this device's copy is the choice it knows (the account's last answer, or a press here; a copy kept
  // before 1.8.3 that is not the plain call is a choice too) — and while it knows none, what the person's role
  // starts with: a crew phone hides the number (*67), everyone else's call is a normal one.
  const device = prefs.callModeUnsent ? prefs.callMode : (knownCallMode(userId) ?? (prefs.callMode !== 'normal' ? prefs.callMode : defaultCallMode(me.data?.role ?? null)));
  const choice = prefs.callModeUnsent || !server ? device : server.mode;
  return { mode: effectiveCallMode(choice, teams), teams };
}

/** `(phone) => href` for an `<a>`, dialled the way the signed-in person chose. */
export function useCallHref(): (phone: string) => string {
  const c = useCalling();
  return useCallback((phone: string) => callLink(phone, c.mode, c.teams).href, [c.mode, c.teams]);
}

export interface CallAnchorProps {
  href: string;
  title?: string;
  'aria-label'?: string;
  'data-call': CallMode;
}

/**
 * `(phone, label?) => props` for an `<a>`: the href, and — when the number is hidden or the call goes through
 * Teams — a title and an accessible name that say so ("Call … — your number is hidden"), so it is never
 * silent. `label` is the link's own accessible name when it has one (Call <name>); without it the number is read.
 */
export function useCallLink(): (phone: string, label?: string) => CallAnchorProps {
  const { t } = useTranslation();
  const c = useCalling();
  return useCallback(
    (phone: string, label?: string) => {
      const l = callLink(phone, c.mode, c.teams);
      const base = { href: l.href, 'data-call': l.via };
      if (l.via === 'normal') return label ? { ...base, 'aria-label': label } : base;
      const said = label ?? t('contact.callNumber', { number: phone });
      return { ...base, title: t(l.via === 'hide' ? 'contact.hiddenTitle' : 'contact.teamsTitle'), 'aria-label': `${said} — ${t(l.via === 'hide' ? 'contact.hiddenHint' : 'contact.teamsHint')}` };
    },
    [c.mode, c.teams, t],
  );
}

/** One save at a time: the switch's own press and the catch-up after being offline never race each other. */
let sending = false;

export type CallModeSaved = { saved: 'account' } | { saved: 'device' } | { saved: 'refused'; why: string };

/**
 * The person's own switch: kept on this device at once, then on their account (PUT /api/me/calling). Offline
 * it stays on the device, marked unsent, and `useCallingSync` sends it when the app is online again.
 */
export function useSetCallMode(): (mode: CallMode) => Promise<CallModeSaved> {
  const qc = useQueryClient();
  const { prefs, setPrefs, userId } = usePrefs();
  const before = prefs.callMode;
  return useCallback(
    async (mode: CallMode) => {
      const known = knownCallMode(userId);
      rememberCallMode(userId, mode);
      setPrefs({ callMode: mode, callModeUnsent: true });
      sending = true;
      try {
        const out = await send<CallingSettings>('/api/me/calling', 'PUT', { mode });
        qc.setQueryData<MeResponse>(['me-extras'], (d) => (d ? { ...d, calling: out, extras: { ...d.extras, callMode: mode } } : d));
        rememberCallMode(userId, out.mode);
        setPrefs({ callMode: out.mode, callModeUnsent: false });
        return { saved: 'account' };
      } catch (e) {
        // the server said no (Teams is not set up, not signed in): back to what it was. Anything else is the network.
        if (e instanceof ApiRequestError && e.status >= 400 && e.status < 500) {
          rememberCallMode(userId, known);
          setPrefs({ callMode: before, callModeUnsent: false });
          return { saved: 'refused', why: e.body?.message ?? e.message };
        }
        return { saved: 'device' };
      } finally {
        sending = false;
      }
    },
    [before, qc, setPrefs, userId],
  );
}

/**
 * Keeps this device's copy in step with the account (mounted once, with the signed-in app): a change made
 * offline is sent as soon as /api/me answers; otherwise the account's choice is copied here for the next
 * time the app is offline.
 */
export function useCallingSync(): void {
  const me = useMeExtras();
  const qc = useQueryClient();
  const { prefs, setPrefs, userId } = usePrefs();
  const server = me.data?.calling ?? null;
  const role = me.data?.role ?? null;
  useEffect(() => {
    if (!server) return;
    if (!prefs.callModeUnsent) {
      // 1.8.3: the account's answer is what this device knows now — so the person's choice stands when the account
      // cannot be read. The settings' own copy follows as before, except on a device with no settings yet whose
      // only difference is what the role starts with (a new crew phone): nothing is written to the settings for that.
      rememberCallMode(userId, server.mode);
      if (prefs.callMode !== server.mode && (hasStoredPrefs(userId) || server.mode !== defaultCallMode(role))) setPrefs({ callMode: server.mode });
      return;
    }
    if (sending) return;
    sending = true;
    send<CallingSettings>('/api/me/calling', 'PUT', { mode: prefs.callMode })
      .then((out) => {
        qc.setQueryData<MeResponse>(['me-extras'], (d) => (d ? { ...d, calling: out, extras: { ...d.extras, callMode: out.mode } } : d));
        rememberCallMode(userId, out.mode);
        setPrefs({ callMode: out.mode, callModeUnsent: false });
      })
      .catch((e: unknown) => {
        // refused: the account's choice stands; the network again: try at the next sign of life
        if (e instanceof ApiRequestError && e.status >= 400 && e.status < 500) {
          rememberCallMode(userId, server.mode);
          setPrefs({ callMode: server.mode, callModeUnsent: false });
        }
      })
      .finally(() => {
        sending = false;
      });
  }, [server, role, userId, prefs.callMode, prefs.callModeUnsent, qc, setPrefs]);
}
